<script lang="ts">
  import { onMount } from 'svelte';
  import type { PwNextRevealProps } from './types';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import { debounce } from '$lib/helpers/browser/utils';

  export let entryId: PwNextRevealProps['entryId'];
  export let slides: PwNextRevealProps['slides'];
  export let stickyTopMargin: PwNextRevealProps['stickyTopMargin'];
  export let textPaddingPercent: PwNextRevealProps['textPaddingPercent'];
  export let stickyTriggerPercent: PwNextRevealProps['stickyTriggerPercent'];
  export let switchColumns: PwNextRevealProps['switchColumns'];

  let loaded = false;
  let stickyIndex: number = 0;
  let iObserver: IntersectionObserver;
  let rObserver: ResizeObserver;
  let stickyImageHolder: HTMLDivElement;
  let textHolderPadding: number;
  let stickyOffsetTop: number = 0;
  const slideTexts: HTMLDivElement[] = [];

  const onIObserve = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        stickyIndex = Number((entry.target as HTMLDivElement).dataset.index);
      }
    });
  };

  const getRootMargin = (): string => {
    // get where the image sticks vertically, centered or the provided top margin
    stickyOffsetTop = Math.max(stickyTopMargin, (window.innerHeight - stickyImageHolder.offsetHeight) / 2);
    // create a 10px trigger area beside the image based on desired vertical percent reached of the image
    const rootMarginTop = stickyOffsetTop + stickyImageHolder.offsetHeight * stickyTriggerPercent;
    const rootMarginBottom = window.innerHeight - rootMarginTop - 10;
    // set the text column vertical padding to be 1/3 image height
    textHolderPadding = stickyImageHolder.offsetHeight * textPaddingPercent;
    // return observer rootMargin
    return `-${rootMarginTop}px 0px -${rootMarginBottom}px 0px`;
  };

  const onResize = debounce(() => {
    if (iObserver) {
      iObserver.disconnect();
    }
    // on larger screens, ANY of the text needs to be intersecting to trigger area to become active
    iObserver = new IntersectionObserver(onIObserve, { rootMargin: getRootMargin(), threshold: 0 });
    slideTexts.forEach((slideText) => {
      iObserver.observe(slideText);
    });
    loaded = true;
  }, 100);

  onMount(() => {
    onResize();

    // when animations are used here, the height of the sticky container is initially 0.
    // we set a resize observer to capture height changes as the assets load and call onResize
    // to recalculate the rootMargin for the IntersectionObserver
    rObserver = new ResizeObserver(() => {
      onResize();
    });

    rObserver.observe(stickyImageHolder);

    return () => {
      if (iObserver) {
        iObserver.disconnect();
      }

      if (rObserver) {
        rObserver.disconnect();
      }
    };
  });
</script>

<svelte:window on:resize={onResize} />
<div
  class="PwNextReveal PwNextReveal-{entryId}-box-properties"
  class:switchColumns
  class:loaded
  data-entry-id={entryId}
  style="
    --reveal-text-padding: {textHolderPadding}px 0px;
    --sticky-offset-top: {stickyOffsetTop}px;"
>
  <div class="revealStickyImageHolder" bind:this={stickyImageHolder}>
    {#each slides as slide, slideIndex}
      <div class="revealImage" class:reveal={slideIndex <= stickyIndex} class:fade={stickyIndex > slideIndex}>
        {#if slide.image}
          <DynamicChildrenComponent children={[slide.image]} />
        {/if}
      </div>
    {/each}
  </div>
  <div class="revealContentHolder">
    {#each slides as slide, slideIndex}
      <div class="revealImageMobile">
        {#if slide.image}
          <DynamicChildrenComponent children={[slide.image]} />
        {/if}
      </div>
      <div
        class="revealText"
        class:active={stickyIndex === slideIndex}
        data-index={slideIndex}
        bind:this={slideTexts[slideIndex]}
      >
        <DynamicChildrenComponent children={slide.content} />
      </div>
    {/each}
  </div>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as media;

  @layer custom-components {
    .PwNextReveal {
      width: var(--container-width-current);
      position: relative;
      margin-left: auto;
      margin-right: auto;
      opacity: 0;
      transition: opacity var(--duration-default);

      &.switchColumns {
        flex-direction: row-reverse;
      }

      &.loaded {
        opacity: 1;
      }

      .revealImageMobile {
        border-radius: var(--container-border-radius);
        overflow: hidden;
      }

      .revealStickyImageHolder {
        display: none;
      }

      @include media.at('small') {
        --split-image-width: 50%;
        --split-text-width: 40%;

        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        max-width: none;
        width: var(--container-width-current);

        .revealStickyImageHolder {
          display: block;
          position: sticky;
          top: var(--sticky-offset-top);
          background-color: transparent;
          width: var(--split-image-width);
          max-width: none;
          margin-bottom: 0;
          border-radius: var(--container-border-radius);
          overflow: hidden;

          .revealImage {
            display: flex;
            justify-content: center;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            opacity: 0;
            transition: opacity var(--duration-default);

            :global(img) {
              border-radius: var(--container-border-radius);
              object-fit: contain;
              max-height: calc(100vh - 72px - var(--space-40));
            }

            &:first-child {
              position: relative; // let the first image set the image container height
            }

            &.reveal {
              opacity: 1;

              &.fade {
                opacity: 0;
              }
            }
          }
        }

        .revealImageMobile {
          display: none;
        }

        .revealContentHolder {
          width: var(--split-text-width);
          max-width: none;
          padding: var(--reveal-text-padding);
          margin: 0;
        }

        .revealText {
          opacity: 0.3;
          transition: opacity var(--duration-default);

          &.active {
            opacity: 1;
          }
        }
      }
    }
  }
</style>
